import { IEquipmentStatusApiModel2 } from '@models/EquipmentStatus.model';
import { pick } from '@utils/pick';
import { IFieldApiModel2 } from './Field.model';
import { IManufacturerApiModel2 } from './Manufacturer.model';
import { IProductApiModel2 } from './Product.model';
import Tag, { ITagApiModel2, ITagLimitWithDesc } from './Tag.model';
import { ITagCategoryApiModel2 } from './TagCategory.model';
import { IUserApiModel2 } from './User.model';
import { parseDate } from '../utils/parseDate/parseDate';
import DataConverter from './DataConverter.model';

import { isAllNull } from '../utils/isAllNull/isAllNull';
import { unwrap } from '../utils/unwrap/unwrap';
import { EquipmentIdentificationStatus } from './Equipment.model';

export type Nullable<T> = T | null;

export interface IEquipmentInventoryApiModel {
  id: string;
  purchasedPrice: number | null;
  purchasedFrom: string | null;
  purchasedDate: string | null; // date
  serial: string | null;
  sla: string | null;
  statusUpdatedAt: string | null; // date
  statusComment: string | null;
  extendedWarrantyPurchased: boolean | null;
  extendedWarrantyLength: string | null; // this is a number right?, used only in onboarding modal  OnBoardingData, as number | "", mostly nulls
  installedSoftwareVersion: string | null;
  installedFirmwareVersion: string | null;
  userId: string | null;
  studioId: string | null;
  vendor: string | null;
  licenseStartDate: string | null;
  licenseEndDate: string | null;
  installDate: string | null;
  firmwareId: string | null;
  equipmentStatusId: string | null;
  productId: string | null; // An equipment may not have a product
  statusUserId: string | null;

  createdAt: string;
  equipmentStatus: [IEquipmentStatusApiModel2];
  users: [IUserApiModel2]; //  1-1 relation
  products: [IProductApiModel2]; //  1-1 relation
  fields: IFieldApiModel2[];
  manufacturer: [IManufacturerApiModel2]; //  1-1 transitive relation from product
  tags: ITagApiModel2[];
  tagCategory: ITagCategoryApiModel2[];
  identificationStatus: EquipmentIdentificationStatus | null;

  importedModel: string | null;
  importedName: string | null;
  importedManufacturerName: string | null;
}

// Update this list if the api field is just assigned
const sharedFields = [
  'purchasedPrice',
  'purchasedFrom',
  'installedSoftwareVersion',
  'installedFirmwareVersion',
  'productId',
  'id',
  'serial',
  'sla',
  'studioId',
  'userId',
  'vendor',
  'equipmentStatusId',
  'fields',
  'extendedWarrantyPurchased',
  'identificationStatus',
  // Field used by field config
  'statusComment',
] as const;
type ISharedFieldNames = typeof sharedFields[number];

type IEquipmentInventoryShared = Pick<
  IEquipmentInventoryApiModel,
  ISharedFieldNames
>;

interface IEquipmentInventoryBase extends IEquipmentInventoryShared {
  purchasedDate: Date | null;
  licenseStartDate: Date | null;
  licenseEndDate: Date | null;
  installDate: Date | null;
  equipmentStatus: IEquipmentStatusApiModel2 | undefined;

  user: IUserApiModel2 | undefined;
  tags: ITagLimitWithDesc[];
  statusUpdatedAt: Date | null;
  // fields used by field configuration
  createdAt: Date | null;
  productName: IProductApiModel2['name'];
  productModel: IProductApiModel2['model'];
  productPage: IProductApiModel2['pageUrl'];
  productDescription: IProductApiModel2['description'];

  manufacturerName: IManufacturerApiModel2['name'];
  manufacturerImg: IManufacturerApiModel2['imageUrl'];
  userName: IUserApiModel2['name']; // Derived from "user"
  equipmentStatusName: IEquipmentStatusApiModel2['name'];
  // Not provided for equipment inventory. Todo: Add productCategory.
  productCategory?: undefined;
  productStatus?: undefined;
  productWarranty?: undefined;

  importedModel: string | null;
  importedName: string | null;
  importedManufacturerName: string | null;
}
export interface IEquipmentInventory extends IEquipmentInventoryBase {
  product: IProductApiModel2;
}

class EquipmentInventoryConverter extends DataConverter<
  IEquipmentInventoryApiModel,
  IEquipmentInventory
> {
  // eslint-disable-next-line class-methods-use-this
  fromApiModel(model: IEquipmentInventoryApiModel): IEquipmentInventory {
    const tagAgg = Tag.rawArrayFromEquipmentApiModel(model);

    const userAgg = unwrap(model.users);
    const statusAgg = unwrap(model.equipmentStatus);
    const shared = pick(model, sharedFields);
    const fieldAgg = shared.fields.filter(field => !isAllNull(field));
    return {
      ...shared,
      purchasedDate: parseDate(model.purchasedDate),
      licenseStartDate: parseDate(model.licenseStartDate),
      licenseEndDate: parseDate(model.licenseEndDate),
      installDate: parseDate(model.installDate),
      equipmentStatus: statusAgg,
      user: userAgg,
      tags: tagAgg,
      fields: fieldAgg,
      statusUpdatedAt: parseDate(model.statusUpdatedAt),
      // Todo: Use unwrap to reduce ambiguity here.
      product: model.products[0],
      // fields used by field configuration
      createdAt: parseDate(model.createdAt),
      productName: model.products[0].name,
      productModel: model.products[0].model,
      productPage: model.products[0].pageUrl,
      productDescription: model.products[0].description,
      manufacturerName: model.manufacturer[0].name,
      manufacturerImg: model.manufacturer[0].imageUrl,
      userName: model.users[0].name,
      equipmentStatusName: model.equipmentStatus[0].name,
      importedModel: model.importedModel,
      importedName: model.importedName,
      importedManufacturerName: model.importedManufacturerName,
    };
  }
}

const EquipmentInventory = new EquipmentInventoryConverter();
export default EquipmentInventory;
